import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Container from '@helios/Container';
import IconButton from '@helios/IconButton';
import { Formik, setNestedObjectValues } from 'formik';
import parse from 'date-fns/parse';
import format from 'date-fns/format';
import logger from '@/utils/logger';
import { NOT_IN_US_LEAD_STATE, US_STATE_CODES, toUSStateCode } from '@/utils/usStates';
import * as yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import ApplicationFormStep from './ApplicationFormStep.js';
import PersonalInformation from './PersonalInformation.js';
import Education from './Education.js';
import ProgramOfInterest from './ProgramOfInterest.js';
import Demographics from './Demographics.js';
import Goals from './Goals.js';
import SchedulePreference from './SchedulePreference.js';
import ApplicationFormReview from './ApplicationFormReview.js';
import ApplicationFormSubmitted from './ApplicationFormSubmitted.js';
import RedirectSlidePreload from './RedirectSlidePreload.js';
import RedirectBuffer from '../RedirectBuffer';
import { useDegreeAwareClassTimes } from './ClassTimeSlots.js';
import { sanitizeHdyhauSnapshot } from './hdyhauConfig.js';
import { CLASS_TIME_SLOTS, CLASS_TIMES_REQUIRED_MESSAGE, EMPTY_CLASS_TIMES, buildClassTimesPayload, classTimesFromSchedules, hasPreferredClassTime, sameClassTimes, splitClassTimeSlots } from './classTimes';
import { PROGRAM_STEP, REVIEW_STEP, SCHEDULE_STEP, SUBMITTED_STEP, displayStepCount, displayStepNumber, nextStep, previousStep, programStepForApplication, resumeStep, stepProgressPercent } from './programStep';
import { useRouter } from 'next/router.js';
import { useAuth } from '../AuthProvider';
import { usePostHog } from 'posthog-js/react';
import { FormattedBrandLogo } from '../FormattedBrandLogo.js';
import { useApplicationEssayMinChars } from '@/hooks/useApplicationEssayMinChars';
import { useMagicLinkRedirect } from '@/hooks/useMagicLinkRedirect';
import useReferral from '@/hooks/useReferral';
import { captureAnalyticsEvent, captureLegacyAnalyticsEvent } from '@/lib/analytics';
import RegularArrowLeftIcon from '@/components/icons/regular/arrow-left';
import RegularHouseIcon from '@/components/icons/regular/house';
import styles from './ApplicationForm.module.scss';
const shortDate = () => yup.date().transform(function (value, originalValue) {
  if (originalValue === '' || originalValue == null) {
    return undefined;
  }
  if (this.isType(originalValue)) {
    return originalValue;
  }
  const date = parse(originalValue, 'MM/yyyy', new Date());
  return this.isType(date) && format(date, 'MM/yyyy') === originalValue ? date : yup.date.INVALID_DATE;
});
const longDate = () => yup.date().transform(function (value, originalValue) {
  if (originalValue === '' || originalValue == null) {
    return undefined;
  }
  if (this.isType(originalValue)) {
    return originalValue;
  }
  const date = parse(originalValue, 'MM/dd/yyyy', new Date());
  return this.isType(date) && format(date, 'MM/dd/yyyy') === originalValue ? date : yup.date.INVALID_DATE;
});
const pastLongDate = longDate().min(new Date(1920, 0, 1), 'Date is too far in the past').max(new Date(), 'Date cannot be in the future');
// https://uibakery.io/regex-library/zip-code
const usZipCode = () => yup.string().matches(/^[0-9]{5}(?:-[0-9]{4})?$/);
// https://uibakery.io/regex-library/ssn
const ssn = () => yup.string().matches(/^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/, {
  excludeEmptyString: true
});
const phoneNumber = () => yup.string().test('valid-phone-number', 'Invalid phone number', function (value) {
  if (!value) return true;
  const phoneNumber = parsePhoneNumberFromString(value);
  return !!phoneNumber?.isPossible();
});

/**
 * One Yup schema per step index (1-5), then the review's two acknowledgements.
 * The program question (`degreeId`) belongs to step 1 in control and to step
 * 3, with the class-time preference it reveals, in the program-step
 * experiment's test variant; control's step 3 asks nothing.
 *
 * @param {boolean} [passwordOptional]
 * @param {number} [minChars]
 * @param {{
 *   programStepVariant?: import('./programStep').ProgramStepVariant,
 *   degrees?: Array<{id: string, name?: string, classTimesOffered?: import('@/types/ClassScheduleTimes').ClassSchedule}>,
 *   degreeAwareClassTimes?: boolean,
 * }} [options]
 */
export const getValidationSchemas = (passwordOptional = false, minChars = 200, {
  programStepVariant = 'control',
  degrees = [],
  degreeAwareClassTimes = false
} = {}) => {
  const minDate = new Date(1950, 0, 1);
  const sanityMaxDate = new Date(new Date().getFullYear() + 10, 11, 31);
  const programOnOwnStep = programStepVariant === 'test';
  const program = {
    degreeId: yup.string().required()
  };
  return [yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    middleName: yup.string(),
    preferredName: yup.string(),
    address: yup.string().required(),
    secondAddress: yup.string(),
    country: yup.string().required(),
    city: yup.string().required(),
    // A US State must be one of the select's options: a lead-prefilled
    // full name ("South Carolina") matches no option, yet used to submit
    // and then never passed the automated initial screen (STX-1095).
    state: yup.string().required().when('country', {
      is: 'US',
      then: schema => schema.oneOf(US_STATE_CODES)
    }),
    zip: yup.string().required().when('country', {
      is: 'US',
      then: () => usZipCode().required()
    }),
    mobilePhone: phoneNumber().required(),
    homePhone: phoneNumber(),
    hearAboutCampus: yup.string().required(),
    ...(programOnOwnStep ? {} : program),
    referredBy: yup.string().when('hearAboutCampus', {
      is: 'referred',
      then: schema => schema.required()
    }),
    myEmployer: yup.string(),
    otherDescription: yup.string(),
    hearAboutCampusFollowUp: yup.string(),
    hearAboutCampusFollowUpDetail: yup.string(),
    password: yup.string().when([], {
      is: () => passwordOptional,
      then: schema => schema,
      otherwise: schema => schema.required()
    }),
    passwordConfirm: yup.string().when([], {
      is: () => passwordOptional,
      then: schema => schema,
      otherwise: schema => schema.required().oneOf([yup.ref('password')])
    })
  }), yup.object().shape({
    highSchoolGrad: yup.string().required(),
    highSchoolGradDate: shortDate().when('highSchoolGrad', {
      is: 'yes',
      then: schema => schema.required().min(minDate, 'Date is too far in the past').max(sanityMaxDate, 'Date is too far in the future')
    }),
    highSchoolName: yup.string().when('highSchoolGrad', {
      is: 'yes',
      then: schema => schema.required()
    }),
    highSchoolGpa: yup.string().when(['ged', 'highSchoolGrad'], {
      is: (ged, highSchoolGrad) => ged === 'yes' || highSchoolGrad === 'yes' || ged === 'no' && highSchoolGrad === 'no',
      then: schema => schema.required()
    }),
    ged: yup.string().when('highSchoolGrad', {
      is: 'no',
      then: schema => schema.required()
    }),
    gedDate: shortDate().when(['ged', 'highSchoolGrad'], {
      is: (ged, highSchoolGrad) => ged === 'yes' && highSchoolGrad === 'no',
      then: schema => schema.required().min(minDate, 'Date is too far in the past').max(sanityMaxDate, 'Date is too far in the future')
    }),
    gedPlanned: yup.string().when(['ged', 'highSchoolGrad'], {
      is: (ged, highSchoolGrad) => ged === 'no' && highSchoolGrad === 'no',
      then: schema => schema.required()
    }),
    termId: yup.string().required(),
    nameMatchesGradDocument: yup.string().when(['ged', 'highSchoolGrad'], {
      is: (ged, highSchoolGrad) => ged === 'yes' || highSchoolGrad === 'yes',
      then: schema => schema.required()
    }),
    nameFromGradDocument: yup.string().when('nameMatchesGradDocument', {
      is: 'no',
      then: schema => schema.required()
    }),
    distanceEducation: yup.string().required()
  }), programOnOwnStep ? yup.object().shape({
    ...program,
    // At least one of the selected program's preferred slots, once a
    // program is chosen (the slots render only then).
    classTimes: yup.mixed().test('atLeastOnePreferred', CLASS_TIMES_REQUIRED_MESSAGE, function (value) {
      const degreeId = this.parent?.degreeId;
      if (!degreeId) return true;
      const degree = degrees?.find(d => d.id === degreeId);
      const {
        preferredSlots
      } = splitClassTimeSlots(degree?.classTimesOffered, degreeAwareClassTimes);
      return hasPreferredClassTime(value, preferredSlots);
    })
  }) : yup.object(), yup.object().shape({
    dob: pastLongDate.required(),
    gender: yup.string().required(),
    pronouns: yup.string(),
    usCitizen: yup.string().required(),
    ssn: ssn().when('usCitizen', {
      is: 'yes',
      then: schema => schema.required()
    }),
    countryOfCitizenship: yup.string().when('usCitizen', {
      is: 'no',
      then: schema => schema.required()
    }),
    alienRegistrationNumber: yup.string(),
    familyFirstCollege: yup.string().required(),
    pastCollegeEnrollment: yup.string().required(),
    pastCollegeName: yup.string().when('pastCollegeEnrollment', {
      is: 'yes',
      then: schema => schema.required()
    }),
    pastCollegeFrom: shortDate().when('pastCollegeEnrollment', {
      is: 'yes',
      then: schema => schema.required().min(minDate, 'Date is too far in the past').max(sanityMaxDate, 'Date is too far in the future')
    }),
    pastCollegeTo: shortDate().when('pastCollegeEnrollment', {
      is: 'yes',
      then: schema => schema.required().min(yup.ref('pastCollegeFrom'), 'End date cannot be before start date').max(sanityMaxDate, 'Date is too far in the future')
    }),
    armedForcesStatus: yup.string().required(),
    armedForcesFrom: shortDate().when('armedForcesStatus', {
      is: value => value !== 'notApplicable',
      then: schema => schema.required().min(minDate, 'Date is too far in the past').max(sanityMaxDate, 'Date is too far in the future')
    }),
    armedForcesTo: shortDate().when('armedForcesStatus', {
      is: value => value !== 'notApplicable',
      then: schema => schema.required().min(yup.ref('armedForcesFrom'), 'End date cannot be before start date').max(sanityMaxDate, 'Date is too far in the future')
    }),
    hispanicOrLatino: yup.string().required(),
    race: yup.string().required(),
    computerAccess: yup.string().required(),
    internetAccess: yup.string().required()
  }), yup.object().shape({
    transferToCollege: yup.bool(),
    startCareer: yup.bool(),
    changeCareer: yup.bool(),
    changeLife: yup.bool(),
    meetPeople: yup.bool(),
    startBusiness: yup.bool(),
    other: yup.bool(),
    otherMotivation: yup.string(),
    // EXPERIMENT: Dynamic minimum character count via PostHog feature flag
    // This code will be reverted after the experiment concludes.
    // See: application-essay-minimum-character-count feature flag
    reasonForCollege: yup.string().required('Required field').min(minChars, `Min ${minChars} characters`).max(800, 'Limit to 800 characters')
  }), yup.object().shape({
    accurateInfoCheck: yup.bool().required().oneOf([true])
  }), yup.object().shape({
    consentToSms: yup.bool().required().oneOf([true])
  })];
};
function ApplicationForm({
  site,
  lead,
  app,
  terms,
  degrees,
  onPrev,
  onSubmit,
  onLogout,
  employer,
  onSubmitClassTimes,
  showTermBanner = true,
  // The program-step experiment's variant for this form, `{variant, source}`
  // (programStep.ts), resolved by the page. Without it: the application's
  // stored variant, else control.
  programStep: programStepProp = null,
  // Written into a new application's form: until a program exists the
  // backend picks the Pardot handler from it.
  location = site === 'sacramento' ? 'sacramento' : 'scholars'
}) {
  // Fixed for the life of the form, so an application never switches layout
  // midway (a create, for one, swaps `app` under a running form).
  const [programStep] = useState(() => programStepProp ?? programStepForApplication(app));
  const variant = programStep.variant;
  const programOnOwnStep = variant === 'test';
  const defaultStep = resumeStep(app, variant);
  const leadDegree = lead?.fieldOfInterest ? degrees.find(d => d.name === lead.fieldOfInterest)?.id : undefined;
  // Control seeds the program from the request-info form, else Business
  // Administration. The test variant's "Program of Interest" step starts on
  // its placeholder unless the request-info form captured a program.
  const defaultDegree = programOnOwnStep ? leadDegree ?? '' : lead?.fieldOfInterest ? leadDegree : degrees.find(d_0 => d_0.name === 'Business Administration')?.id ?? '';
  // Test variant: the class times a resumed application already saved, when
  // `GET /campus-app` returns them.
  const [restoredClassTimes] = useState(() => programOnOwnStep && app ? classTimesFromSchedules(app.preferredClassSchedule, app.optionalClassSchedule) : undefined);
  // Test variant: the program and ticks the schedule endpoint last saved, so
  // the review re-saves the class times only when they changed.
  const savedClassTimesRef = useRef(restoredClassTimes ? {
    degreeId: app?.degreeId,
    classTimes: restoredClassTimes
  } : null);
  const [step, setStep] = useState(defaultStep);
  const [snapshot, setSnapshot] = useState(() => {
    // Whether a resumed application had chosen a state at all: a saved value
    // the select cannot show becomes '' below, and that must surface as the
    // field's own error rather than be papered over by the lead's state.
    let hadSavedState = false;
    let defaultSnapshot = {
      // step 1
      firstName: '',
      lastName: '',
      middleName: '',
      preferredName: '',
      address: '',
      secondAddress: '',
      country: '',
      city: '',
      state: '',
      zip: '',
      mobilePhone: '',
      homePhone: '',
      termId: null,
      hearAboutCampus: '',
      hearAboutCampusFollowUp: '',
      hearAboutCampusFollowUpDetail: '',
      degreeId: defaultDegree,
      referredBy: '',
      myEmployer: '',
      otherDescription: '',
      password: '',
      passwordConfirm: '',
      // step 2
      highSchoolGrad: '',
      highSchoolGradDate: '',
      highSchoolName: '',
      highSchoolGpa: '',
      ged: '',
      gedDate: '',
      gedPlanned: '',
      nameMatchesGradDocument: '',
      nameFromGradDocument: '',
      distanceEducation: '',
      // step 3 (test variant): the class-time ticks. They are saved through
      // the schedule endpoint and never sent with the application.
      ...(programOnOwnStep ? {
        classTimes: {
          ...EMPTY_CLASS_TIMES
        }
      } : {}),
      // step 4
      dob: '',
      gender: '',
      pronouns: 'preferNotToSay',
      usCitizen: '',
      ssn: '',
      countryOfCitizenship: '',
      alienRegistrationNumber: '',
      familyFirstCollege: '',
      pastCollegeEnrollment: '',
      pastCollegeName: '',
      pastCollegeFrom: '',
      pastCollegeTo: '',
      armedForcesStatus: 'notApplicable',
      armedForcesFrom: '',
      armedForcesTo: '',
      hispanicOrLatino: '',
      race: '',
      guardianFirstName: '',
      guardianLastName: '',
      guardianEmail: '',
      // step 5
      transferToCollege: '',
      startCareer: '',
      changeCareer: '',
      changeLife: '',
      meetPeople: '',
      startBusiness: '',
      other: '',
      otherMotivation: '',
      // review
      accurateInfoCheck: false,
      consentToSms: false
    };
    if (app) {
      defaultSnapshot = {
        ...defaultSnapshot,
        ...sanitizeHdyhauSnapshot(app.form),
        termId: app.termId,
        degreeId: programOnOwnStep ? app.degreeId ?? defaultDegree : app.degreeId,
        ...(programOnOwnStep ? {
          classTimes: {
            ...(restoredClassTimes ?? EMPTY_CLASS_TIMES)
          }
        } : {})
      };
      // An application saved before the backend normalized the State can
      // resume with a full name that the select cannot show and the schema
      // now rejects; a non-US "state" stays free text.
      hadSavedState = !!defaultSnapshot.state;
      if (defaultSnapshot.country === 'US') {
        defaultSnapshot.state = toUSStateCode(defaultSnapshot.state);
      }
    }
    if (lead) {
      if (!defaultSnapshot.firstName) {
        defaultSnapshot.firstName = lead.firstName;
      }
      if (!defaultSnapshot.lastName) {
        defaultSnapshot.lastName = lead.lastName;
      }
      if (!defaultSnapshot.mobilePhone) {
        defaultSnapshot.mobilePhone = lead.phone;
      }
      // Leads carry the state as the lead form or ad platform sent it, often
      // the full name; the select needs the code (or nothing). A lead who
      // said they do not live in the US gets neither a state nor a country,
      // and a resumed application that already chose another country keeps
      // it.
      const outsideUS = !!defaultSnapshot.country && defaultSnapshot.country !== 'US';
      if (!defaultSnapshot.state && !hadSavedState && !outsideUS && lead.state !== NOT_IN_US_LEAD_STATE) {
        defaultSnapshot.state = toUSStateCode(lead.state);
        defaultSnapshot.country = 'US';
      }
    }
    if (employer) {
      defaultSnapshot.hearAboutCampus = 'myEmployer';
      // Employer attribution: every form key is posted to Pardot, so the
      // forever21 seed stays although the employment questions are gone.
      if (employer.id === 'forever21') {
        defaultSnapshot.myEmployer = employer.id;
        defaultSnapshot.employer = employer.id;
      }
    }
    return defaultSnapshot;
  });
  const {
    refCode
  } = useReferral();
  useEffect(() => {
    if (refCode && !snapshot.hearAboutCampus) {
      setSnapshot(s => ({
        ...s,
        hearAboutCampus: 'referred',
        referredBy: refCode
      }));
    }
  }, [refCode]);
  const displayStep = displayStepNumber(step, variant);
  const displayCount = displayStepCount(variant);
  const progress = stepProgressPercent(step, variant);
  const {
    email
  } = useAuth();
  const STEP_NAMES = {
    1: 'Personal Information',
    2: 'Education',
    3: 'Program of Interest',
    4: 'Demographics',
    5: 'Goals',
    6: 'Review'
  };
  const maxCompletedStepRef = useRef(app?.form.step ?? 0);
  const capturedBusinessEventsRef = useRef(new Set());
  // Test variant: the program a review saved ahead of its class times (see
  // the submit), so a retry after a failed schedule save does not save it
  // again before `app` catches up.
  const reviewProgramSavedRef = useRef(undefined);
  const [classTimesSaveFailed, setClassTimesSaveFailed] = useState(false);
  // Test variant: set by the submit once it (and its class times) saved;
  // starts the hand-off to the platform.
  const [handingOff, setHandingOff] = useState(false);
  // Test variant: the hand-off gave up (see below). Never cleared while the
  // form is mounted.
  const [handOffFailed, setHandOffFailed] = useState(false);
  const posthog = usePostHog();
  const router = useRouter();
  const pendingStep = defaultStep > step ? defaultStep : step;
  // Existing applicants have already created their credentials. The API does
  // not return passwords, so a resumed step-one application must not require
  // the applicant to re-enter one before they can continue.
  const passwordDisabled = Boolean(app?.id) || pendingStep > 1;
  const minChars = useApplicationEssayMinChars();
  const degreeAwareClassTimes = useDegreeAwareClassTimes();
  const validationSchemas = getValidationSchemas(passwordDisabled, minChars, {
    programStepVariant: variant,
    degrees,
    degreeAwareClassTimes
  });
  // Test variant: the hand-off to the platform follows the submit directly
  // (control's follows the class-times step). The magic link is fetched only
  // once the submit saved, because each fetch logs the applicant in on the
  // backend (a device, a token, a login event). One retry and no refetch on
  // focus or reconnect: a failure falls back to the Submitted screen within a
  // couple of seconds and stays there.
  const magicLinkQuery = useMagicLinkRedirect({
    enabled: handingOff && !handOffFailed && !!email,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  // No link is coming (it failed, or there is no signed-in email to ask for
  // one), so the buffer would wait at 0: the Submitted screen instead, latched
  // the first time. The query's own state cannot decide it: the Submitted
  // screen's StartAdmissionTasks reads the same query with react-query's
  // defaults, and its mount refetches the failed query, which turns the error
  // back into loading (and later maybe success). A live `isError` would bring
  // the buffer back each time; the latch keeps the applicant on the Submitted
  // screen, whose own button opens the portal once a link exists.
  if (handingOff && !handOffFailed && (!email || magicLinkQuery?.isError)) {
    setHandOffFailed(true);
  }
  const classTimesNeedSaving = values => {
    const saved = savedClassTimesRef.current;
    return !saved || saved.degreeId !== values.degreeId || !sameClassTimes(saved.classTimes, values.classTimes);
  };

  // Test variant: the ticks go to the schedule endpoint after the save that
  // carries the program, split by that program's offered slots.
  const saveClassTimes = async values_0 => {
    const degree = degrees?.find(d_1 => d_1.id === values_0.degreeId);
    const {
      preferredSlots,
      optionalSlots
    } = splitClassTimeSlots(degree?.classTimesOffered, degreeAwareClassTimes);
    const {
      payload,
      preferred,
      optional
    } = buildClassTimesPayload(values_0.classTimes, {
      preferredSlots,
      optionalSlots,
      degreeAware: degreeAwareClassTimes
    });
    await onSubmitClassTimes(payload);
    savedClassTimesRef.current = {
      degreeId: values_0.degreeId,
      classTimes: {
        ...EMPTY_CLASS_TIMES,
        ...values_0.classTimes
      }
    };
    captureLegacyAnalyticsEvent(posthog, 'Class Times Submitted', {
      applicationId: app?.id,
      degreeId: values_0.degreeId,
      degreeName: degree?.name,
      preferredSlotsSelected: CLASS_TIME_SLOTS.filter(slot => preferred[slot]).length,
      optionalSlotsSelected: CLASS_TIME_SLOTS.filter(slot_0 => optional[slot_0]).length,
      preferred,
      optional,
      flag_on: degreeAwareClassTimes,
      source: 'program_step',
      // 3 from the Program of Interest step, 6 from a re-save on the review.
      step_number: step,
      program_step_variant: variant
    });
  };
  const completeStep = () => {
    if (step > maxCompletedStepRef.current) {
      maxCompletedStepRef.current = step;
      captureLegacyAnalyticsEvent(posthog, 'Application Step Completed', {
        step_number: step,
        step_name: STEP_NAMES[step],
        program_step_variant: variant
      });
    }
  };
  const goBack = formik => {
    setSnapshot(s_0 => ({
      ...s_0,
      ...formik.values
    }));
    if (formik.status?.nextBlocked) {
      formik.setStatus({
        ...formik.status,
        nextBlocked: false
      });
    }
    setClassTimesSaveFailed(false);
    setStep(s_1 => previousStep(s_1, variant));
  };
  if (handingOff && !handOffFailed) {
    // Counts down at once and redirects when the link arrives, during the
    // countdown or after it (RedirectBuffer follows its redirectUrl).
    return <RedirectBuffer redirectUrl={magicLinkQuery?.data} countdown={5} />;
  }
  return <>
      {/* Test variant: the buffer's slides load while the applicant reviews
          (a preload mints nothing; the magic link waits for the submit). */}
      {programOnOwnStep && step === REVIEW_STEP && <RedirectSlidePreload />}
      {step <= REVIEW_STEP && <Formik validateOnBlur={false} enableReinitialize initialValues={snapshot} validationSchema={step < REVIEW_STEP ? validationSchemas[step - 1] : validationSchemas.reduce((a, b) => a.concat(b))} onSubmit={async (values_1, {
      setTouched
    }) => {
      const {
        classTimes,
        ...answers
      } = values_1;
      const vals = {
        ...answers,
        transferToCollege: String(values_1.transferToCollege),
        startCareer: String(values_1.startCareer),
        changeCareer: String(values_1.changeCareer),
        changeLife: String(values_1.changeLife),
        meetPeople: String(values_1.meetPeople),
        startBusiness: String(values_1.startBusiness),
        other: String(values_1.other),
        step: Math.max(step, app?.form.step ?? 0)
      };
      const isNew = !app?.id;
      if (isNew) {
        // Sent once, on the create, and kept by the application: the
        // site's location and the experiment variant it runs.
        vals.location = location;
        vals.programStepVariant = variant;
        vals.programStepVariantSource = programStep.source ?? 'fallback';
      } else {
        // Later saves never resend them (a resumed form carries the
        // stored values); the backend keeps what the create stored.
        delete vals.location;
        delete vals.programStepVariant;
        delete vals.programStepVariantSource;
      }
      if (programOnOwnStep && (step < PROGRAM_STEP || !vals.degreeId)) {
        // The test variant asks for the program on step 3: earlier
        // saves send no degreeId at all, never an empty one (the
        // backend keeps a saved degree when the key is absent).
        delete vals.degreeId;
      }
      const savesClassTimes = programOnOwnStep && (step === PROGRAM_STEP || step === REVIEW_STEP && classTimesNeedSaving(values_1));
      if (savesClassTimes) {
        setClassTimesSaveFailed(false);
      }
      const reportSaveFailure = err => {
        captureAnalyticsEvent(posthog, 'application_flow_failed', {
          application_id: app?.id,
          site,
          stage: step === REVIEW_STEP ? 'submission' : isNew ? 'creation' : 'save',
          step_number: step,
          program_step_variant: variant
        });
        logger.error(err, {
          description: 'Application form submit failed',
          source: 'ApplicationForm',
          step
        });
      };

      // The step's save, and the GTM and PostHog events that follow a
      // successful start or submit (never a failed one).
      const saveStep = async () => {
        try {
          const savedApp = await onSubmit(vals);
          if (step < REVIEW_STEP) {
            setTouched({});
            if (isNew) {
              const applicationId = savedApp?.id;
              if (process.env.NODE_ENV === 'production') {
                // push below fields to GTM datalayer; GTM's snippet
                // defines it only where its id is set
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'Application started',
                  email,
                  mobilePhone: savedApp.form.mobilePhone,
                  firstName: savedApp.form.firstName,
                  lastName: savedApp.form.lastName
                });
                if (applicationId && !capturedBusinessEventsRef.current.has(applicationId)) {
                  const captured = captureLegacyAnalyticsEvent(posthog, 'Application Started', {
                    applicationId,
                    site,
                    email,
                    mobilePhone: savedApp.form.mobilePhone,
                    firstName: savedApp.form.firstName,
                    lastName: savedApp.form.lastName,
                    program_step_variant: variant
                  });
                  if (captured) {
                    capturedBusinessEventsRef.current.add(applicationId);
                  }
                }
              }
            }
          } else if (process.env.NODE_ENV === 'production') {
            // push below fields to GTM datalayer; GTM's snippet
            // defines it only where its id is set
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'applicationSubmit',
              email,
              firstName: vals.firstName,
              lastName: vals.lastName,
              mobilePhone: vals.mobilePhone,
              address: vals.address,
              country: vals.country,
              city: vals.city,
              state: vals.state,
              zip: vals.zip
            });
            window.dataLayer.push({
              event: 'Application submitted',
              email,
              mobilePhone: savedApp.form.mobilePhone,
              firstName: savedApp.form.firstName,
              lastName: savedApp.form.lastName,
              state: savedApp.form.state
            });
          }
          return true;
        } catch (err_0) {
          reportSaveFailure(err_0);
          return false;
        }
      };

      // Test variant: the class times, after a save that stored their
      // program. A failure keeps the applicant on the step with the
      // message on the slot group; the form stays dirty, so the next
      // attempt saves again.
      const saveStepClassTimes = async () => {
        try {
          await saveClassTimes(values_1);
          return true;
        } catch (err_1) {
          setClassTimesSaveFailed(true);
          captureAnalyticsEvent(posthog, 'application_flow_failed', {
            application_id: app?.id,
            site,
            stage: 'schedule_preference',
            step_number: step,
            program_step_variant: variant
          });
          logger.error(err_1, {
            description: 'Schedule preference submit failed',
            source: 'ApplicationForm',
            step
          });
          return false;
        }
      };
      if (step === REVIEW_STEP && savesClassTimes) {
        // Test variant, review: the application is marked submitted
        // (step 6) only once its class times are saved, so a failed
        // schedule save leaves it open at the review (a submitted one
        // cannot be reopened or changed). The backend checks the
        // schedule against the stored program, so a program changed
        // here is saved first, at the step the application already
        // has.
        const storedProgram = reviewProgramSavedRef.current ?? app?.degreeId;
        if (vals.degreeId && vals.degreeId !== storedProgram) {
          try {
            await onSubmit({
              ...vals,
              step: Math.min(app?.form.step ?? REVIEW_STEP - 1, REVIEW_STEP - 1)
            });
            reviewProgramSavedRef.current = vals.degreeId;
          } catch (err_2) {
            reportSaveFailure(err_2);
            return;
          }
        }
        if (!(await saveStepClassTimes())) return;
        if (!(await saveStep())) return;
      } else {
        if (!(await saveStep())) return;
        // Step 3: the save that stored the program comes first.
        if (savesClassTimes && !(await saveStepClassTimes())) return;
      }
      setSnapshot(s_2 => ({
        ...s_2,
        ...vals,
        ...(classTimes ? {
          classTimes
        } : {})
      }));
      completeStep();
      if (programOnOwnStep && step === REVIEW_STEP) {
        // The buffer shows at once; the Submitted screen (the next
        // step) is the fallback when the link fails.
        setHandingOff(true);
      }
      setStep(s_3 => nextStep(s_3, variant));
    }}>
          {formik_0 => <form onSubmit={e => {
        if (step === REVIEW_STEP) {
          formik_0.handleSubmit(e);
          return;
        }
        e.preventDefault();
        formik_0.validateForm().then(errors => {
          formik_0.setTouched(setNestedObjectValues(errors, true));
          const blocked = Object.keys(errors);
          if (blocked.length === 0 && formik_0.status?.nextBlocked) {
            // A clean attempt ends the blocked state (CodeRabbit on
            // #1495): the next visit to a step starts unflagged.
            formik_0.setStatus({
              ...formik_0.status,
              nextBlocked: false
            });
          }
          if (blocked.length > 0) {
            // Diagnostic (2026-10-09): a blocked Next click shows
            // nothing for fields without their own message, so
            // name the fields in PostHog (names and lengths only)
            // and let the essay show its "Min" error.
            formik_0.setStatus({
              ...(formik_0.status || {}),
              nextBlocked: true
            });
            captureAnalyticsEvent(posthog, 'application_step_blocked', {
              application_id: app?.id,
              site,
              step_number: step,
              step_name: STEP_NAMES[step],
              fields: blocked,
              essay_length: formik_0.values.reasonForCollege?.length ?? 0,
              essay_min: minChars,
              program_step_variant: variant
            });
            return;
          }
          if (!formik_0.dirty) {
            completeStep();
            setStep(s_4 => nextStep(s_4, variant));
            return;
          }
          formik_0.handleSubmit(e);
        }).catch(err_3 => {
          captureAnalyticsEvent(posthog, 'application_step_validation_error', {
            application_id: app?.id,
            site,
            step_number: step,
            message: (err_3 instanceof Error ? err_3.message : typeof err_3 === 'string' ? err_3 : 'Unknown validation error').slice(0, 200),
            program_step_variant: variant
          });
        });
      }}>
              <div className={styles['corner-actions']}>
                <IconButton icon={<RegularHouseIcon />} label="Home" href="/" />
                <IconButton icon={<RegularArrowLeftIcon />} label="Back" disabled={formik_0.isSubmitting} onClick={() => {
            if (step === 1) {
              onPrev();
              return;
            }
            goBack(formik_0);
          }} />
              </div>
              <Container width="sm" gutters="compact">
                <div className={styles['step']}>
                  <FormattedBrandLogo site={site} employer={employer} />
                  {step == 1 && <>
                      <h1 className={styles['step-title']}>
                        Personal Information
                      </h1>{' '}
                    </>}
                  {step == 2 && <h1 className={styles['step-title']}>Education</h1>}
                  {step === PROGRAM_STEP && <h1 className={styles['step-title']}>
                      Program of Interest
                    </h1>}
                  {step === 4 && <h1 className={styles['step-title']}>Demographics</h1>}
                  {step === 5 && <h1 className={styles['step-title']}>Your Goals</h1>}
                  {step === REVIEW_STEP && <h1 className={classNames(styles['step-title'], styles['step-title--wide'])}>
                      Review your application
                    </h1>}
                  {step < REVIEW_STEP && <>
                      <div className={styles['progress']}>
                        <h5 className={styles['progress__label']}>
                          Step {displayStep} of {displayCount}
                        </h5>
                        <div className={styles['progress__track']}>
                          <div className={styles['progress__bar']} role="progressbar" style={{
                    '--progress': `${progress}%`
                  }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                      <ApplicationFormStep isSubmitting={formik_0.isSubmitting} firstStep={step === 1} onPrev={() => {
                if (step === 1) {
                  onPrev();
                  return;
                }
                goBack(formik_0);
              }}>
                        {step === 1 && <PersonalInformation email={email} degrees={degrees} passwordDisabled={passwordDisabled} employerDisabled={!!employer} referralCodeDisabled={!!refCode} showProgram={!programOnOwnStep} />}
                        {step === 2 && <Education terms={terms} />}
                        {step === PROGRAM_STEP && <ProgramOfInterest degrees={degrees} site={site} saveFailed={classTimesSaveFailed} />}
                        {step === 4 && <Demographics />}
                        {step === 5 && <Goals />}
                      </ApplicationFormStep>
                    </>}
                  {step === REVIEW_STEP && <ApplicationFormReview site={site} terms={terms} email={email} degrees={degrees} isSubmitting={formik_0.isSubmitting} passwordDisabled={passwordDisabled} employerDisabled={!!employer} programStepVariant={variant} classTimesSaveFailed={classTimesSaveFailed} />}
                </div>
              </Container>
            </form>}
        </Formik>}
      {step === SCHEDULE_STEP && (() => {
      const selectedDegree = degrees?.find(d_2 => d_2.id === app?.degreeId);
      return <SchedulePreference site={site} employer={employer} onSubmitClassTimes={onSubmitClassTimes} setStep={setStep} degreeId={app?.degreeId} degreeName={selectedDegree?.name} classTimesOffered={selectedDegree?.classTimesOffered} applicationId={app?.id} programStepVariant={variant} />;
    })()}
      {step === SUBMITTED_STEP && <ApplicationFormSubmitted site={site} app={app} employer={employer} onLogout={onLogout} onBackToApps={() => {
      router.reload();
    }} showTermBanner={showTermBanner} />}
    </>;
}
export default ApplicationForm;